import axios from 'axios';

// Production: nginx terminates TLS and proxies /api to the backend (no :5000 in the browser).
export const BASE_URL = 'https://terracoolservices.com';
// export const BASE_URL = 'http://localhost:5000';

export const getUploadUrl = (imagePath) => {
  if (!imagePath) return null;
  const path = String(imagePath);
  if (path.startsWith('http') || path.startsWith('blob:')) {
    return path;
  }
  const cleanPath = path.replace(/^\/+/, '');
  // Same-origin relative URL so nginx (prod) or dev proxy can serve uploads.
  return `/${cleanPath}`;
};

const axiosInstance = axios.create({
  baseURL: BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  // Let axios handle JSON parsing automatically - don't override transformResponse
  // transformResponse: axios.defaults.transformResponse.concat([(data) => {
  //   // Custom transformations can go here if needed
  //   return data;
  // }]),
});

axiosInstance.interceptors.request.use((config) => {
  const token = sessionStorage.getItem('token');
  if (token) {
    config.headers['access-token'] = token;
  }
  return config;
}, (error) => {
  return Promise.reject(error);
});

axiosInstance.interceptors.response.use((response) => {
  // Simple logging for debugging - only for specific endpoints

  return response;
}, (error) => {
  if (error.response && error.response.status === 401) {
    // Don't redirect for login requests - let the component handle the error
    if (error.config && error.config.url && error.config.url.includes('/api/users/login')) {
      return Promise.reject(error);
    }
    sessionStorage.clear();
    window.location.href = '/';
  }
  return Promise.reject(error);
});

export const login = (data) => {
  return axiosInstance.post('/api/users/login', data);
};

// There is no 'register' export in this file.
// If you need a register API, add it here, for example:
// export const register = (data) => {
//   return axiosInstance.post('/api/users/register', data);
// };

// Added staff API functions
export const getStaff = (tenant_id) => {
  return axiosInstance.get(`/api/staff`);
};

export const createStaff = (data) => {
  return axiosInstance.post('/api/staff', data);
};

export const updateStaff = (id, data) => {
  return axiosInstance.put(`/api/staff`, { ...data, id });
};

export const deleteStaff = (id) => {
  return axiosInstance.delete('/api/staff', { data: { id } });
};

// Technicians API functions
export const getTechnicians = () => {
  return axiosInstance.get('/api/technicians');
};

export const createTechnician = (data) => {
  return axiosInstance.post('/api/technicians/add', data);
};

export const updateTechnician = (data) => {
  return axiosInstance.put('/api/technicians/update', data);
};

export const deleteTechnician = (id) => {
  return axiosInstance.delete(`/api/technicians/${id}`);
};

// Complaints API functions
export const getComplaints = () => {
  return axiosInstance.get('/api/complaints');
};

export const lookupCustomerByPhone = (phone) => {
  return axiosInstance.get('/api/complaints/customer-lookup', {
    params: { phone },
  });
};

export const createComplaint = (data) => {
  // Check if data contains files (FormData) or regular data
  const isFormData = data instanceof FormData;
  
  return axiosInstance.post('/api/complaints/add', data, {
    headers: isFormData ? {
      'Content-Type': 'multipart/form-data'
    } : {
      'Content-Type': 'application/json'
    }
  });
};

export const updateComplaint = (data) => {
  // Check if data contains files (FormData) or regular data
  const isFormData = data instanceof FormData;
  
  return axiosInstance.put('/api/complaints/update', data, {
    headers: isFormData ? {
      'Content-Type': 'multipart/form-data'
    } : {
      'Content-Type': 'application/json'
    }
  });
};

export const deleteComplaint = (id) => {
  return axiosInstance.delete(`/api/complaints/${id}`);
};

export const getServiceReportPdf = (complaintId) => {
  return axiosInstance.get(`/api/complaints/${complaintId}/service-report`, {
    responseType: 'blob',
  });
};

export const getComplaintsByTechnician = (technicianName) => {
  return axiosInstance.get(`/api/complaints/technician/${technicianName}`);
};

// Inventory API functions
export const getInventory = () => {
  return axiosInstance.get('/api/inventory');
};

export const createInventory = (data) => {
  // Send material_name, present_stock, overall_stock
  return axiosInstance.post('/api/inventory/add', {
    material_name: data.material_name,
    present_stock: data.present_stock,
    overall_stock: data.overall_stock
  });
};

export const updateInventory = (data) => {
  // Send id, material_name, present_stock, overall_stock
  return axiosInstance.put('/api/inventory/update', {
    id: data.id,
    material_name: data.material_name,
    present_stock: data.present_stock,
    overall_stock: data.overall_stock
  });
};

export const deleteInventory = (id) => {
  return axiosInstance.delete(`/api/inventory/${id}`);
};

export const getInventoryUsage = (inventoryId) => {
  return axiosInstance.get(`/api/inventory/${inventoryId}/usage`);
};

export const returnInventoryUsage = (usageId) => {
  return axiosInstance.post(`/api/inventory/usage/${usageId}/return`);
};

export const getComplaintInventory = (complaintId) => {
  return axiosInstance.get(`/api/mobile/complaints/${complaintId}/inventory`);
};

// Services API functions
export const getServices = () => {
  return axiosInstance.get('/api/services');
};

export const createService = (data) => {
  return axiosInstance.post('/api/services/add', data);
};

export const updateService = (data) => {
  return axiosInstance.put('/api/services/update', data);
};

export const deleteService = (id) => {
  return axiosInstance.delete(`/api/services/${id}`);
};

// Live tracking API functions
export const getTechniciansLocation = () => {
  return axiosInstance.get(`/api/technicians/locations`);
};

// Get technician logs and locations API function
export const getTechnicianLogsAndLocations = (technicianId, fromDate, toDate) => {
  return axiosInstance.get(`/api/technicians/${technicianId}/logs-locations?from_date=${fromDate}&to_date=${toDate}`);
};

export default axiosInstance;
export { axiosInstance };